import { Box, Button } from "@mui/material";
import { Download } from "@mui/icons-material";

export interface PdfViewerProps {
  /** URL del PDF (directa o blob:). */
  src?: string;
  /** Titulo accesible del iframe. */
  title?: string;
  /** Nombre sugerido para la descarga. */
  fileName?: string;
  /** Texto del boton de descarga. */
  downloadLabel?: string;
  /** Alto del visor. */
  height?: number | string;
}

/**
 * Lightweight PDF viewer: renders a PDF in the browser's built-in viewer via an
 * <iframe> (page navigation, zoom, fullscreen, print, download — all native),
 * plus an explicit download button. No PDF library required.
 *
 * Pass `src` as a same-origin or blob URL. For protected/framed endpoints the
 * caller should fetch the bytes (with auth) and pass an object URL, which also
 * sidesteps X-Frame-Options.
 *
 * @param {string} src             PDF URL (direct or blob:).
 * @param {string} [title]         Accessible title for the iframe.
 * @param {string} [fileName]      Suggested name for the download.
 * @param {string} [downloadLabel] Download button label.
 * @param {number|string} [height] Viewer height (default 700).
 */
export const PdfViewer = ({
  src,
  title = "document",
  fileName,
  downloadLabel = "Download",
  height = 700,
}: PdfViewerProps) => {
  if (!src) return null;

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Button
          component="a"
          href={src}
          download={fileName}
          startIcon={<Download />}
          variant="outlined"
          sx={{
            color: "#1976D2",
            borderColor: "#1976D2",
            "&:hover": { borderColor: "#1565C0", backgroundColor: "rgba(25, 118, 210, 0.04)" },
          }}
        >
          {downloadLabel}
        </Button>
      </Box>
      <Box
        component="iframe"
        src={src}
        title={title}
        sx={{
          width: "100%",
          height,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          display: "block",
        }}
      />
    </Box>
  );
};
